// Phase 31 Plan 31-01 — POST /api/match/{id}/place-anchor wrapper.
//
// Mirrors apps/web/src/net/turn-submit.ts contract. The wrapper's job is solely:
//   1. Generate a UUIDv4 Idempotency-Key (or honor a caller-supplied key).
//   2. POST to `/api/match/{id}/place-anchor` with the body shape `{ cell_idx }`.
//   3. Bubble the typed response through apiFetch's standard handling:
//      - 5xx single-retry with SAME key (FIFO-50 cache hit per Phase 3).
//      - 4xx (non-401) surfaces ApiError; user re-tap generates a NEW key.
//      - 401 clears auth (AuthInvalidError).
//
// Phase 26 D-LOCK / RUNE-15 — server gates this endpoint on the caller's
// `requires_anchor_placement === true` flag. Server-side `validateAnchor-
// Placement` is canonical (DEC-021); the client wrapper does NOT pre-check
// eligibility (ergonomic-only client eligibility lives in AnchorPlacement-
// Overlay).
//
// Phase 31 T-31-csrf mitigation: Bearer JWT in Authorization header via
// existing apiFetch — no cookie auth path. Idempotency-key composite-PK on
// server protects against cross-user replay (SEV-01 fix; Phase 26 lock).

import { apiFetch } from './client'
import { createIdempotencyKey } from './idempotency'

export interface PlaceAnchorBody {
  readonly cell_idx: number
}

/**
 * Server returns the caller's next PlayerView (full or projected) as the
 * 200 response. The shape is the same JSON.parse-able PlayerView the WS
 * STATE_SNAPSHOT delivers; the wrapper types it loosely here and the
 * caller narrows downstream (mirrors turn-submit.ts:TurnResponse).
 */
export interface PlaceAnchorResponse {
  readonly [k: string]: unknown
}

export interface PlaceAnchorOptions {
  /** If provided, skips internal generation. Caller is responsible for UUIDv4 format. */
  idempotencyKey?: string
}

/**
 * POST /api/match/{matchId}/place-anchor with an `Idempotency-Key` header.
 *
 * - Generates a fresh UUIDv4 key per call (caller may override).
 * - On 5xx, apiFetch retries ONCE with the SAME key (FIFO-50 cache hit).
 * - On 4xx (non-401), apiFetch surfaces ApiError; caller can re-submit
 *   to generate a NEW key.
 * - On 401, apiFetch clears the auth bearer + throws AuthInvalidError.
 */
export async function placeAnchor(
  matchId: string,
  body: PlaceAnchorBody,
  opts: PlaceAnchorOptions = {},
): Promise<PlaceAnchorResponse> {
  const idempotencyKey = opts.idempotencyKey ?? createIdempotencyKey()
  return apiFetch<PlaceAnchorResponse>(
    `/api/match/${matchId}/place-anchor`,
    'POST',
    {
      body,
      idempotencyKey,
    },
  )
}
